import React, { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import Button from '../../ui/Button';
import { RESOURCES } from '../../../config/advancedRolesConfig';

/**
 * Modal pour ajouter un nouveau rôle
 * @param {Object} props - Propriétés du modal
 * @param {boolean} props.isOpen - Si le modal est ouvert
 * @param {Function} props.onClose - Callback de fermeture
 * @param {Function} props.onAdd - Callback d'ajout
 * @returns {ReactElement} Modal d'ajout de rôle
 */
const AddRoleModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    id: '',
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du rôle est requis';
    }
    
    if (!formData.id.trim()) {
      newErrors.id = "L'identifiant du rôle est requis";
    } else if (!/^[a-z0-9_]+$/.test(formData.id)) {
      newErrors.id = "L'identifiant ne doit contenir que des lettres minuscules, chiffres et underscores";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Créer le nouveau rôle avec des permissions de base (lecture seule)
    const basePermissions = {};
    Object.values(RESOURCES).forEach(resource => {
      basePermissions[resource] = {
        read: true,
        write: false,
        create: false,
        delete: false,
        manage: false
      };
    });

    const newRole = {
      id: formData.id.toLowerCase(),
      name: formData.name.trim(),
      description: formData.description.trim(),
      permissions: basePermissions,
      level: 50, // Niveau par défaut
      isSystem: false,
      isActive: true
    };

    onAdd(newRole);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      id: '',
    });
    setErrors({});
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay avec animation */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
        onClick={handleClose}
      />
      
      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Modal content avec animation */}
        <div 
          className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-300 ease-in-out animate-fade-in-up max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* En-tête avec gradient */}
          <div className="border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Ajouter un nouveau rôle
              </h2>
              <button
                onClick={handleClose}
                className="rounded-lg p-2 text-gray-400 hover:bg-white hover:text-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du rôle *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={cn(
                  'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                  errors.name ? 'border-red-300' : 'border-gray-300',
                  'bg-white text-gray-900'
                )}
                placeholder="Ex: Commercial, Technicien..."
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Identifiant technique *
              </label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => handleChange('id', e.target.value)}
                className={cn(
                  'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                  errors.id ? 'border-red-300' : 'border-gray-300',
                  'bg-white text-gray-900'
                )}
                placeholder="Ex: commercial, technician..."
              />
              {errors.id && (
                <p className="text-red-500 text-sm mt-1">{errors.id}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Utilisez des lettres minuscules, chiffres et underscores uniquement
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                placeholder="Description des responsabilités de ce rôle..."
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-blue-800 mb-2">
                ⓘ Information
              </h4>
              <p className="text-xs text-blue-600">
                Le nouveau rôle sera créé avec des permissions de base en lecture seule.
                Vous pourrez ajuster les permissions après la création.
              </p>
            </div>
          </form>

          {/* Pied de page */}
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Créer le rôle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRoleModal;
